import { useState, useEffect, useRef, useCallback } from "react";

/** Build full logo URL via backend image proxy. */
function getCompanyLogoSrc(logoPath) {
  if (!logoPath || typeof logoPath !== "string") return null;
  const base = process.env.REACT_APP_BACKEND_URL;
  if (!base) return logoPath;
  return `${base}/utils/image?imageUrl=${encodeURIComponent(logoPath)}`;
}

/**
 * Encapsulates company logo URL and load state. Shows placeholder until image
 * has loaded or on error; avoids setState after unmount.
 * Prefers company.logo, then company.main_logo.
 */
export function useCompanyLogo(company) {
  const logoPath = company?.logo || company?.main_logo || null;
  const logoUrl = logoPath ? getCompanyLogoSrc(logoPath) : null;
  const [logoError, setLogoError] = useState(false);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    setLogoError(false);
    setLogoLoaded(false);
  }, [logoUrl]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleLoad = useCallback(() => {
    if (isMountedRef.current) setLogoLoaded(true);
  }, []);

  const handleError = useCallback(() => {
    if (isMountedRef.current) setLogoError(true);
  }, []);

  return {
    logoUrl,
    logoError,
    logoLoaded,
    handleLoad,
    handleError,
  };
}
